import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import AppLoader from "./AppLoader";
import {
  buildMonthYearLabel,
  formatAxisCompactCurrency,
  formatCurrency,
} from "../utils/formatters";

function formatActionTime(timestamp) {
  if (!timestamp) {
    return "";
  }

  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return timestamp;
  }

  return parsed.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function CustomerActionPanel({
  customer,
  timeline,
  isLoading,
  inline = false,
  terminology = {},
}) {
  const [expandedActionId, setExpandedActionId] = useState(null);
  const activityPlural = terminology.activity_plural_label || "Activities";
  const spendLabel = terminology.spend_label || "Spend";
  const revenueLabel = terminology.revenue_label || "Revenue";

  const miniTrend = useMemo(() => {
    const points = timeline?.spend_trend || [];
    const fallbackYear = new Date().getFullYear();
    const categories = points.map((point) =>
      point.month_label || buildMonthYearLabel(point.month, fallbackYear)
    );
    const data = points.map((point) => {
      const value = Number(point.revenue);
      return Number.isFinite(value) ? value : 0;
    });
    const isSinglePointSource = points.length === 1;
    const minValue = data.length ? Math.min(...data) : 0;
    const maxValue = data.length ? Math.max(...data) : 0;
    const isFlatSeries = data.length > 0 && minValue === maxValue;
    const flatPadding = isFlatSeries ? Math.max(1, Math.abs(maxValue) * 0.12) : 0;

    return {
      series: [{ name: revenueLabel, data }],
      options: {
        chart: {
          type: "line",
          height: 180,
          sparkline: { enabled: false },
          toolbar: { show: false },
          animations: { enabled: false },
          redrawOnParentResize: true,
          redrawOnWindowResize: false,
          background: "transparent",
          fontFamily: "Segoe UI, sans-serif",
          foreColor: "#64748b",
        },
        noData: {
          text: `No ${spendLabel.toLowerCase()} trend data`,
          align: "center",
          verticalAlign: "middle",
          style: { color: "#64748b", fontSize: "12px" },
        },
        xaxis: {
          type: "category",
          categories,
          labels: {
            show: true,
            rotate: -35,
            formatter: (value) => value,
            style: { fontSize: "11px" },
          },
          axisBorder: { show: true, color: "#d8e1ef" },
          axisTicks: { show: true, color: "#d8e1ef" },
        },
        yaxis: {
          min: isFlatSeries ? minValue - flatPadding : undefined,
          max: isFlatSeries ? maxValue + flatPadding : undefined,
          forceNiceScale: true,
          labels: {
            show: true,
            formatter: (value) => formatAxisCompactCurrency(value),
            style: { fontSize: "11px" },
          },
        },
        grid: { show: true, borderColor: "#e2e8f0", strokeDashArray: 3 },
        stroke: { curve: "smooth", width: isSinglePointSource ? 0 : 3 },
        markers: {
          size: isSinglePointSource ? 10 : 5,
          strokeWidth: 2,
          strokeColors: "#ffffff",
          hover: { size: 9 },
        },
        colors: ["#1a6ef5"],
        dataLabels: { enabled: false },
        tooltip: {
          theme: "light",
          marker: { show: true },
          x: {
            formatter: (_value, { dataPointIndex }) =>
              categories[dataPointIndex] || "",
          },
          y: { formatter: (value) => formatCurrency(value) },
        },
      },
    };
  }, [revenueLabel, spendLabel, timeline?.spend_trend]);

  const groupedActions = useMemo(() => {
    const actions = timeline?.actions || [];
    const groups = new Map();

    actions.forEach((action) => {
      const key = action.campaign_id || `single-${action.action_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          campaignId: action.campaign_id || null,
          source: action.source || "segment_overview",
          timestamp: action.timestamp,
          entries: [],
        });
      }
      groups.get(key).entries.push(action);
    });

    return Array.from(groups.values());
  }, [timeline?.actions]);

  if (!customer) {
    return null;
  }

  const visits = timeline?.visits || [];

  const panelLoading = (label) => (
    <AppLoader variant="compact" title="Loading" message={label} className="panel-loading" />
  );

  return (
    <section className={`customer-action-panel${inline ? " inline" : ""}`}>
      <div className="customer-action-grid">
        <div className="customer-action-card">
          <div className="customer-action-title">Last 5 {activityPlural}</div>
          {isLoading ? (
            panelLoading(`Loading ${activityPlural.toLowerCase()}...`)
          ) : visits.length ? (
            <ul className="visit-list">
              {visits.map((visit) => (
                <li key={visit.bill_id} className="visit-item">
                  <div className="visit-row">
                    <span className="mono-text visit-date">{visit.visit_date || "-"}</span>
                    <span className="mono-text visit-amount">{formatCurrency(visit.amount)}</span>
                  </div>
                  <div className="visit-by">by {visit.billed_by || "Unknown"}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="customer-action-empty">No {activityPlural.toLowerCase()} found.</div>
          )}
        </div>

        <div className="customer-action-card">
          <div className="customer-action-title">{spendLabel} Trend (All Time)</div>
          {isLoading ? (
            panelLoading("Loading trend...")
          ) : !timeline?.spend_trend?.length ? (
            <div className="customer-action-empty">No {spendLabel.toLowerCase()} trend data available.</div>
          ) : (
            <Chart
              key={`${customer.customer_id}-${timeline?.spend_trend?.length || 0}`}
              options={miniTrend.options}
              series={miniTrend.series}
              type="line"
              height={180}
            />
          )}
        </div>

        <div className="customer-action-card">
          <div className="customer-action-title">Action Log</div>
          <div className="action-log-caption">Campaign history from Segment Overview actions.</div>

          {isLoading ? (
            panelLoading("Loading actions...")
          ) : groupedActions.length ? (
            <ul className="action-campaign-list">
              {groupedActions.map((group) => (
                <li key={group.id} className="action-campaign-item">
                  <div className="action-campaign-head">
                    <div className="action-campaign-title-wrap">
                      <span className="action-log-source">{group.source}</span>
                      <span className="action-campaign-title">
                        {group.campaignId ? `Campaign ${group.campaignId.slice(0, 8)}` : "Single action"}
                      </span>
                    </div>
                    <span className="mono-text action-log-time">{formatActionTime(group.timestamp)}</span>
                  </div>
                  <div className="action-campaign-count">{group.entries.length} log item(s)</div>

                  <ul className="action-log-list">
                    {group.entries.map((action) => (
                      <li key={action.action_id} className="action-log-item">
                        <button
                          type="button"
                          className="action-log-toggle"
                          onClick={() =>
                            setExpandedActionId((current) =>
                              current === action.action_id ? null : action.action_id
                            )
                          }
                        >
                          <div className="action-log-head">
                            <span className="action-log-title">{action.title}</span>
                            <span className="action-log-expand">{expandedActionId === action.action_id ? "Hide details" : "View details"}</span>
                          </div>
                        </button>
                        {expandedActionId === action.action_id ? (
                          <div className="action-log-details">
                            {action.note ? <div className="action-log-note">{action.note}</div> : null}
                            {action.message_body ? (
                              <div className="action-log-message">{action.message_body}</div>
                            ) : (
                              <div className="action-log-message muted">No detailed message saved.</div>
                            )}
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            <div className="customer-action-empty">No segment actions logged yet.</div>
          )}
        </div>
      </div>
    </section>
  );
}
